import { Button } from '@mui/material';
import { motion } from 'framer-motion';
import React, { useCallback } from 'react';

import CompleteIcon from '../../../assets/baseball-complete.svg';
import IncompleteIcon from '../../../assets/baseball-incomplete.svg';
import { AnalyticsEvent, useAnalytics } from '../../../components/Providers/AnalyticsProvider';
import { useFetchData } from '../../../hooks/useFetchData';
import { FolderView, ProjectConfigStatus } from '../../../Types/enums';
import { projectConfigStatusPathUrl } from '../../../util/routesUrls';
import { useDrawer } from '../../Providers/DrawerProvider';
import ProjectOverviewDrawer, {
  ProjectConfigStatusColors,
} from './ProjectOverviewDrawer/ProjectOverviewDrawer';

interface ProjectStatusButtonProps {
  selectedView?: string;
  projectId: string;
  projectName: string;
}

const ProjectStatusButton: React.FC<ProjectStatusButtonProps> = ({
  projectId,
  projectName,
  selectedView,
}) => {
  const { openDrawer, addOnClose } = useDrawer();
  const { data: status, refetch } = useFetchData<ProjectConfigStatus>({
    urlPath: projectConfigStatusPathUrl(projectId),
  });
  const analytics = useAnalytics();

  const onClick = useCallback(() => {
    analytics.event(
      AnalyticsEvent.WM_IA_PROJECT_STATUS_CLICK,
      {
        id: projectId,
      },
      {
        projectId,
      }
    );
    // Register the refetch function to be called when the drawer closes
    addOnClose(refetch);

    openDrawer({
      anchor: 'right',
      content: <ProjectOverviewDrawer projectName={projectName} />,
    });
  }, [openDrawer, projectName, projectId, addOnClose, refetch, analytics]);

  const isComplete = status === ProjectConfigStatus.COMPLETE;

  return (
    <Button
      variant="outlined"
      color="primary"
      onClick={onClick}
      sx={{
        ...(selectedView === FolderView.CHAT
          ? {
              position: 'absolute',
              right: '35px',
              top: 'calc(90% - 230px)',
              transform: 'translateY(-50%)',
            }
          : {
              position: 'fixed',
              bottom: 50,
              right: 50,
            }),

        border: `1px solid ${
          isComplete ? '#00A3FF' : ProjectConfigStatusColors[ProjectConfigStatus.INCOMPLETE]
        }`,
        backgroundColor: 'white',
        borderRadius: '10px',
        padding: '10px',
        zIndex: 9999,
        '&:hover': {
          backgroundColor: 'white',
          borderColor: isComplete
            ? '#00A3FF'
            : ProjectConfigStatusColors[ProjectConfigStatus.INCOMPLETE],
        },
        '&:focus': {
          outline: 'none', // Removes the default focus outline
        },
      }}
    >
      <motion.img
        src={isComplete ? CompleteIcon : IncompleteIcon}
        style={{ width: '40px' }}
        animate={
          !isComplete
            ? {
                rotate: [0, -10, 10, -10, 10, 0], // Rotates back and forth
              }
            : {}
        }
        transition={
          !isComplete
            ? {
                repeat: Infinity,
                repeatDelay: 3, // Pause for 3 seconds before restarting
                duration: 0.5,
                ease: 'easeInOut',
              }
            : {}
        }
      />
    </Button>
  );
};

export default ProjectStatusButton;
